/**
 * The draw engine: validates, picks, animates, commits, celebrates.
 * Recording is per session, not per draw — see recorder.js and main.js.
 *
 * FAIRNESS & NO-DUPLICATES (see CLAUDE.md "Invariants"):
 *  1. The pool is always `eligibleEntries(entries, winners)` — previous
 *     winners (any tier, voided or not) are excluded.
 *  2. The winner is chosen with `pickRandom` (crypto RNG) BEFORE any
 *     animation; the animation only visualises that result.
 *  3. `commit` re-checks `hasWon` against the latest state before saving.
 */
import { celebrate } from '../fx/confetti.js';
import * as audio from '../fx/audio.js';
import { eligibleEntries, hasWon, countPeople } from '../lib/entries.js';
import { pickRandom, uid } from '../lib/random.js';
import { allTiersFull, isTierFull, nextOpenTierId } from '../lib/tiers.js';
import { announce, fmt, sleep } from '../ui/dom.js';
import { toast } from '../ui/toast.js';

const DRAW_ALL_PAUSE_MS = 2200;

/**
 * Whether a draw can run right now, and why not.
 * @returns {{ok: boolean, reason: string, pool: object[], tier: object|undefined}}
 */
export function drawStatus(state, entries) {
  const tier = state.tiers.find((t) => t.id === state.activeTierId);
  const pool = eligibleEntries(entries, state.winners);
  const fail = (reason) => ({ ok: false, reason, pool, tier });

  if (!state.comments.length) return fail('Load comments to start.');
  if (!tier) return fail('Add a prize tier to start.');
  if (allTiersFull(state.tiers, state.winners)) return fail('All prizes have been drawn 🎉');
  if (isTierFull(tier, state.winners)) return fail(`“${tier.name}” is complete — pick another tier.`);
  if (!entries.length) return fail('No comments match your filters.');
  if (!pool.length) return fail('No eligible entries left — everyone has already won or been filtered out.');

  const people = countPeople(pool);
  const odds =
    people === pool.length
      ? `${fmt(pool.length)} eligible ${pool.length === 1 ? 'person' : 'people'} · equal chance each`
      : `${fmt(pool.length)} eligible entries from ${fmt(people)} people · more comments = more chances`;
  return { ok: true, reason: odds, pool, tier };
}

/** Nothing is left to draw: every prize is drawn, or nobody eligible remains. */
export function isGiveawayComplete(state, entries) {
  return allTiersFull(state.tiers, state.winners) || (entries.length > 0 && !eligibleEntries(entries, state.winners).length);
}

/**
 * @param {{
 *   store: ReturnType<import('../state.js').createStore>,
 *   getEntries: (state) => object[],
 *   views: {slot: object, wheel: object, countdown: object},
 *   ui: {setBusy, setAutoRun, showReveal, hideReveal},
 * }} deps
 */
export function createDrawEngine({ store, getEntries, views, ui }) {
  let busy = false;
  let autoRun = false;

  function commit(winner, tier, poolSize, mode) {
    const latest = store.get();
    const liveTier = latest.tiers.find((t) => t.id === tier.id);
    if (!liveTier) {
      toast('That prize tier was removed during the draw — result discarded.', { type: 'error' });
      return null;
    }
    if (hasWon(winner.username, latest.winners)) {
      // Unreachable by construction; kept as a last line of defence.
      toast(`@${winner.username} has already won — result discarded. Please draw again.`, { type: 'error' });
      return null;
    }
    if (isTierFull(liveTier, latest.winners)) {
      toast(`“${liveTier.name}” is already complete.`, { type: 'error' });
      return null;
    }

    const record = {
      id: uid(),
      tierId: liveTier.id,
      tierName: liveTier.name,
      prize: liveTier.prize,
      username: winner.username,
      displayName: winner.displayName || winner.username,
      comment: winner.comment,
      commentId: winner.id,
      avatar: winner.avatar,
      drawnAt: new Date().toISOString(),
      mode,
      poolSize,
      voided: false,
    };
    const winners = [...latest.winners, record];
    const activeTierId = isTierFull(liveTier, winners)
      ? (nextOpenTierId(latest.tiers, winners, liveTier.id) ?? liveTier.id)
      : liveTier.id;
    store.set({ winners, activeTierId }, { source: 'draw', newWinnerId: record.id });
    return record;
  }

  /** Draws one winner for the active tier. Resolves true on success. */
  async function drawOne() {
    if (busy) return false;
    const state = store.get();
    const status = drawStatus(state, getEntries(state));
    if (!status.ok) {
      toast(status.reason);
      return false;
    }

    busy = true;
    ui.setBusy(true);
    audio.unlockAudio();
    ui.hideReveal();

    try {
      const { pool, tier } = status;
      const winner = pickRandom(pool); // decided here — before any animation

      const view = views[state.mode] ?? views.slot;
      await view.spin({
        pool,
        winner,
        duration: state.duration * 1000,
        onTick: audio.tick,
        onBeat: audio.countdownBeep, // only the countdown view beats
      });

      const record = commit(winner, tier, pool.length, state.mode);
      if (!record) return false;

      audio.fanfare();
      celebrate();
      ui.showReveal(record);
      announce(`Winner: ${record.displayName}, @${record.username}, ${record.tierName}`);
      return true;
    } catch (err) {
      console.error(err);
      toast(`Draw failed: ${err.message}`, { type: 'error' });
      return false;
    } finally {
      busy = false;
      ui.setBusy(false);
    }
  }

  /** Draws the remaining slots of the active tier one after another. */
  async function drawAll() {
    if (busy || autoRun) return;
    const tierId = store.get().activeTierId;
    autoRun = true;
    ui.setAutoRun(true);
    try {
      while (autoRun) {
        if (!(await drawOne())) break;
        const after = store.get();
        if (!autoRun || after.activeTierId !== tierId || !drawStatus(after, getEntries(after)).ok) break;
        await sleep(DRAW_ALL_PAUSE_MS);
        if (!autoRun) break;
      }
    } finally {
      autoRun = false;
      ui.setAutoRun(false);
    }
  }

  function stopAutoRun() {
    autoRun = false;
    ui.setAutoRun(false);
  }

  return { drawOne, drawAll, stopAutoRun, isBusy: () => busy, isAutoRunning: () => autoRun };
}
